export interface PollingOptions<T> {
  fetchFunction: () => Promise<T>;
  interval: number;
  runImmediately?: boolean;
  onSuccess?: (data: T) => void;
  onError?: (error: unknown) => void;
  onStatusChange?: (isPolling: boolean) => void;
}

export interface PollingController {
  start: () => void;
  stop: () => void;
  isPolling: () => boolean;
  destroy: () => void;
}

/**
 * Creates a polling controller that periodically calls a fetch function
 *
 * @template T The type of data returned by the fetch function
 * @param {PollingOptions<T>} options - Configuration options for the polling
 * @returns {PollingController} Controller object with methods to manage polling
 */
export function createPollingController<T>({
  fetchFunction,
  interval,
  runImmediately = false,
  onSuccess = () => {},
  onError = (err) => {
    console.error('Polling error:', err);
  },
  onStatusChange = () => {},
}: PollingOptions<T>): PollingController {
  let timeoutId: NodeJS.Timeout | null = null;
  let active = false;
  let destroyed = false;

  const fetchData = async (): Promise<void> => {
    if (!active || destroyed) return;

    try {
      const result = await fetchFunction();
      if (!destroyed) {
        onSuccess(result);
      }
    } catch (err: unknown) {
      if (!destroyed) {
        onError(err);
      }
    }
  };

  const scheduleNext = (): void => {
    if (!active || destroyed) return;

    // Clear any existing timeout
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
    // Schedule next poll
    timeoutId = setTimeout(() => {
      fetchData().finally(() => {
        if (active && !destroyed) {
          scheduleNext();
        }
      });
    }, interval);
  };

  const start = (): void => {
    if (active || destroyed) return;
    active = true;
    onStatusChange(true);

    if (runImmediately) {
      fetchData().finally(() => {
        if (active && !destroyed) {
          scheduleNext();
        }
      });
    } else {
      scheduleNext();
    }
  };

  const stop = (): void => {
    if (!active || destroyed) return;

    active = false;
    onStatusChange(false);

    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  const isPolling = (): boolean => {
    return active;
  };

  const destroy = (): void => {
    if (destroyed) return;

    active = false;
    destroyed = true;

    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  return {
    start,
    stop,
    isPolling,
    destroy,
  };
}
